import { Helmet } from 'react-helmet-async';

// =============================================================================
// SeoHead — preenche title/description/OG/JSON-LD da página atual via Helmet.
//
// Funciona pra crawlers que rodam JS (Googlebot, Bingbot moderno · ~95% do
// tráfego SEO orgânico). Pra crawlers de compartilhamento social
// (facebookexternalhit, WhatsApp, Twitterbot) o ideal seria SSR/pré-render,
// mas isso fica pra uma evolução futura · pelo menos o `og:image` default
// no index.html garante que o link tenha SOME representação visual.
//
// Uso:
//   <SeoHead
//     title="Risco Zero · NexoFácil"
//     description="..."
//     ogImage="https://..."
//     noindex={false}
//     jsonLd={{ '@context': 'https://schema.org', '@type': 'LocalBusiness', ... }}
//   />
// =============================================================================

export type SeoHeadProps = {
  /** Document title. Se omitido, o <title> default do index.html prevalece. */
  title?: string;
  description?: string;
  /** URL absoluta da imagem pra OG/Twitter cards. */
  ogImage?: string;
  /** URL canônica da página · ajuda Google a deduplicar variações. */
  canonical?: string;
  /** When true, adiciona <meta name="robots" content="noindex,nofollow">. */
  noindex?: boolean;
  /**
   * Structured data JSON-LD. Pode ser um objeto único ou array de objetos
   * (Google aceita ambos). Tipa como unknown pra evitar acoplar o Helmet ao
   * shape específico do schema.org.
   */
  jsonLd?: unknown;
};

export function SeoHead({
  title,
  description,
  ogImage,
  canonical,
  noindex,
  jsonLd,
}: SeoHeadProps) {
  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter cards · usa og:* como fallback nativo, mas explicito ajuda */}
      <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
